/**
 * Singleton sync engine that drains the score queue to Supabase.
 * Processes items sequentially (FIFO) to preserve ordering
 * (e.g., upsert before score_taken for Six Pack).
 * Retries indefinitely with exponential backoff.
 */

import { onlineManager } from '@tanstack/react-query'
import { queryClient } from '../lib/queryClient'
import { upsertScore, deleteScore, updateScoreTaken } from '../api/games'
import { dequeueScore, markSyncing, markPending, getQueueStats, recoverStuckSyncing } from './scoreQueue'
import logger from './logger'

const QUEUE_KEY = 'press_score_queue'

let processing = false
let scheduled = false

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]')
  } catch {
    return []
  }
}

/**
 * Process the next pending item in the queue.
 * Runs sequentially — one at a time, FIFO order.
 */
export async function processQueue() {
  if (processing) {
    scheduled = true
    return
  }

  if (!onlineManager.isOnline()) return

  // Recover items stuck in 'syncing' from a previous interrupted cycle
  // (e.g., app killed mid-API call). Safe because all operations are idempotent.
  const recovered = recoverStuckSyncing()
  if (recovered > 0) {
    logger.sync('Recovered stuck syncing items', { recovered })
  }

  processing = true
  logger.sync('Queue processing started', getQueueStats())

  try {
    while (true) {
      const queue = readQueue()
      const next = queue.find((item) => item.status === 'pending')
      if (!next) break
      if (!onlineManager.isOnline()) break

      markSyncing(next.id)

      try {
        if (next.type === 'upsert') {
          await upsertScore({
            game_id: next.game_id,
            player_id: next.player_id,
            hole_number: next.hole_number,
            strokes: next.strokes,
          })
        } else if (next.type === 'delete') {
          await deleteScore({
            game_id: next.game_id,
            player_id: next.player_id,
            hole_number: next.hole_number,
          })
        } else if (next.type === 'score_taken') {
          await updateScoreTaken({
            game_id: next.game_id,
            player_id: next.player_id,
            hole_number: next.hole_number,
            taken: next.score_taken,
          })
        }

        // Success — refetch server data first (so it includes the synced score),
        // THEN remove from queue. This prevents a gap where the score is in
        // neither the queue nor the server data during re-render.
        try {
          await queryClient.refetchQueries({ queryKey: ['game', next.game_id] })
        } catch {
          // Refetch failed — safe to dequeue anyway since the write succeeded.
          // The score is on the server; next natural refetch will pick it up.
        }
        dequeueScore(next.id)
      } catch (error) {
        const retries = (next.retries || 0) + 1
        logger.sync(`Failed to sync item ${next.id}`, { type: next.type, gameId: next.game_id, retryCount: retries, error: error?.message })
        markPending(next.id) // increments retries

        // After 5+ retries, create a Sentry issue — score may be stuck
        if (retries >= 5) {
          logger.capture(new Error(`Score sync stuck after ${retries} retries`), {
            itemId: next.id, type: next.type, gameId: next.game_id,
            playerId: next.player_id, hole: next.hole_number, retries,
            queueStats: getQueueStats(), isOnline: onlineManager.isOnline(),
          })
        }

        // Backoff: min(1000 * 2^retries, 60000)
        const delay = Math.min(1000 * Math.pow(2, retries), 60000)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  } finally {
    processing = false
    logger.sync('Queue processing finished', getQueueStats())
    if (scheduled) {
      scheduled = false
      processQueue()
    }
  }
}

// ---------------------------------------------------------------------------
// Event listeners — trigger queue processing
// ---------------------------------------------------------------------------

function startListeners() {
  // When new items are enqueued
  window.addEventListener('score-queue-changed', () => {
    processQueue()
  })

  // When connectivity is restored
  onlineManager.subscribe(() => {
    if (onlineManager.isOnline()) {
      processQueue()
    }
  })
}

// ---------------------------------------------------------------------------
// Background sync for native (Capacitor)
// ---------------------------------------------------------------------------

function setupBackgroundSync() {
  const isNative =
    typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()

  if (isNative) {
    import('@capacitor/app')
      .then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            // App resumed — drain queue
            logger.sync('App resumed, draining queue', getQueueStats())
            processQueue()
          } else {
            // App backgrounded — attempt to drain with background task
            import('@capawesome/capacitor-background-task')
              .then(({ BackgroundTask }) => {
                const taskId = BackgroundTask.beforeExit(async () => {
                  await processQueue()
                  BackgroundTask.finish({ taskId })
                })
              })
              .catch(() => {
                // Plugin not available — best-effort, queue persists in localStorage
              })
          }
        })
      })
      .catch(() => {
        // @capacitor/app not available
      })
  }
}

// ---------------------------------------------------------------------------
// Init — call once at app startup
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Watchdog D: Score sync queue stuck
// Every 60s while the app is active, check for items older than 5 minutes.
// ---------------------------------------------------------------------------

function startStuckQueueWatchdog() {
  setInterval(() => {
    if (!onlineManager.isOnline()) return
    const queue = readQueue()
    const now = Date.now()
    const stale = queue.filter((item) => now - item.created_at > 5 * 60 * 1000)
    if (stale.length > 0) {
      const oldest = Math.max(...stale.map((i) => now - i.created_at))
      logger.capture(new Error('Score queue items stuck >5min while online'), {
        staleCount: stale.length, oldestAgeMs: oldest,
        queueStats: getQueueStats(), isOnline: true,
      })
    }
  }, 60 * 1000)
}

let initialized = false

export function initSyncEngine() {
  if (initialized) return
  initialized = true
  startListeners()
  setupBackgroundSync()
  startStuckQueueWatchdog()
  // Drain anything left from a previous session
  processQueue()
}
